import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import styles from './AIInstructies.module.css';

const OUTPUT_LABELS = {
  sbar:              'SBAR',
  artsconsult:       'Artsconsult',
  rapportage:        'Verpleegkundige Rapportage',
  dienst_overdracht: 'Dienst-overdracht',
  familie:           'Familiecommunicatie',
  overplaatsing:     'Overplaatsingsoverdracht',
};

const OUTPUT_KLEUREN = {
  sbar:              '#2D2B6B',
  artsconsult:       '#1a6fa8',
  rapportage:        '#1A6B3C',
  dienst_overdracht: '#B85C00',
  familie:           '#7B2D8B',
  overplaatsing:     '#CC0000',
};

export default function AIInstructies({ organisaties }) {
  const [geselecteerdeOrg, setGeselecteerdeOrg] = useState('');
  const [instructies, setInstructies]           = useState([]);
  const [geselecteerde, setGeselecteerde]       = useState(null);
  const [laden, setLaden]                       = useState(false);
  const [versies, setVersies]                   = useState([]);
  const [versiesLaden, setVersiesLaden]         = useState(false);
  const [toonVersies, setToonVersies]           = useState(false);

  const [systeemPrompt, setSysteemPrompt]   = useState('');
  const [gebruikerPrompt, setGebruikerPrompt] = useState('');
  const [notitie, setNotitie]               = useState('');
  const [opslaan, setOpslaan]               = useState(false);
  const [gewijzigd, setGewijzigd]           = useState(false);

  useEffect(() => {
    laadInstructies();
  }, [geselecteerdeOrg]);

  async function laadInstructies() {
    setLaden(true);
    setGeselecteerde(null);
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      const res = await axios.get('/api/ai-instructies', { params });
      setInstructies(res.data.instructies);
    } catch {
      toast.error('Instructies laden mislukt.');
    } finally {
      setLaden(false);
    }
  }

  function selecteerInstructie(instructie) {
    if (gewijzigd && !window.confirm('U heeft onopgeslagen wijzigingen. Weet u zeker dat u wilt doorgaan?')) return;
    setGeselecteerde(instructie);
    setSysteemPrompt(instructie.systeem_prompt);
    setGebruikerPrompt(instructie.gebruiker_prompt);
    setNotitie('');
    setGewijzigd(false);
    setToonVersies(false);
    setVersies([]);
  }

  async function laadVersies() {
    if (!geselecteerde) return;
    setVersiesLaden(true);
    setToonVersies(true);
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      const res = await axios.get(`/api/ai-instructies/${geselecteerde.output_type}/versies`, { params });
      setVersies(res.data.versies);
    } catch {
      toast.error('Versies laden mislukt.');
    } finally {
      setVersiesLaden(false);
    }
  }

  async function handleOpslaan() {
    if (!geselecteerde) return;
    setOpslaan(true);
    try {
      await axios.put(`/api/ai-instructies/${geselecteerde.output_type}`, {
        systeem_prompt:   systeemPrompt,
        gebruiker_prompt: gebruikerPrompt,
        organisatie_id:   geselecteerdeOrg || null,
        notitie:          notitie || null,
      });
      toast.success('Instructie opgeslagen.');
      setGewijzigd(false);
      setNotitie('');
      laadInstructies();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Opslaan mislukt.');
    } finally {
      setOpslaan(false);
    }
  }

  async function handleTerugzetten(versieId, versieNr) {
    if (!window.confirm(`Weet u zeker dat u wilt terugzetten naar versie ${versieNr}? De huidige versie wordt gearchiveerd.`)) return;
    try {
      await axios.post(`/api/ai-instructies/${geselecteerde.output_type}/terugzetten/${versieId}`, {
        organisatie_id: geselecteerdeOrg || null,
      });
      toast.success(`Teruggesteld naar versie ${versieNr}.`);
      laadInstructies();
      setToonVersies(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Terugzetten mislukt.');
    }
  }

  async function handleVerwijderOverride() {
    if (!geselecteerdeOrg) return;
    if (!window.confirm('Weet u zeker dat u de organisatie-specifieke instructie wilt verwijderen? De platformstandaard wordt dan gebruikt.')) return;
    try {
      await axios.delete(`/api/ai-instructies/${geselecteerde.output_type}/override`, {
        data: { organisatie_id: geselecteerdeOrg }
      });
      toast.success('Organisatie-instructie verwijderd. Platformstandaard wordt gebruikt.');
      laadInstructies();
      setGeselecteerde(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verwijderen mislukt.');
    }
  }

  return (
    <div className={styles.wrap}>
      {/* Org selector */}
      <div className={styles.orgBalk}>
        <select value={geselecteerdeOrg}
          onChange={e => setGeselecteerdeOrg(e.target.value)}
          className={styles.orgSelect}>
          <option value="">Platformstandaard</option>
          {organisaties.map(o => <option key={o.id} value={o.id}>{o.naam}</option>)}
        </select>
        {!geselecteerdeOrg && (
          <span className={styles.orgInfo}>Platformstandaard geldt voor alle organisaties zonder eigen instructie</span>
        )}
        {geselecteerdeOrg && (
          <span className={styles.orgInfo} style={{color:'#1A6B3C'}}>
            Organisatie-specifieke instructies — overschrijven de platformstandaard
          </span>
        )}
      </div>

      <div className={styles.layout}>
        {/* Lijst van outputtypen */}
        <div className={styles.lijst}>
          {laden ? (
            <div className={styles.laadTekst}>Laden...</div>
          ) : (
            instructies.map(instructie => (
              <button key={instructie.output_type}
                className={`${styles.typeKnop} ${geselecteerde?.output_type === instructie.output_type ? styles.actief : ''}`}
                onClick={() => selecteerInstructie(instructie)}>
                <div className={styles.typeHeader}>
                  <span className={styles.typeNaam}
                    style={{ color: OUTPUT_KLEUREN[instructie.output_type] }}>
                    {OUTPUT_LABELS[instructie.output_type]}
                  </span>
                  <span className={`${styles.bronBadge} ${instructie.bron === 'organisatie' ? styles.bronOrg : styles.bronPlatform}`}>
                    {instructie.bron === 'organisatie' ? 'Organisatie' : 'Platform'}
                  </span>
                </div>
                <div className={styles.typeVersie}>Versie {instructie.versie}</div>
              </button>
            ))
          )}
        </div>

        {/* Editor */}
        <div className={styles.editor}>
          {!geselecteerde ? (
            <div className={styles.leeg}>
              <div style={{fontSize:32,marginBottom:8}}>✍️</div>
              <p>Selecteer een outputtype om de AI-instructies te bekijken en te bewerken.</p>
            </div>
          ) : (
            <>
              <div className={styles.editorHeader}>
                <div>
                  <h3 className={styles.editorTitel}
                    style={{ color: OUTPUT_KLEUREN[geselecteerde.output_type] }}>
                    {OUTPUT_LABELS[geselecteerde.output_type]}
                  </h3>
                  <span className={styles.versieInfo}>Versie {geselecteerde.versie}</span>
                </div>
                <div className={styles.editorActies}>
                  <button className={styles.knopSecundair} onClick={laadVersies}>
                    🕐 Versiegeschiedenis
                  </button>
                  {geselecteerde.bron === 'organisatie' && geselecteerdeOrg && (
                    <button className={`${styles.knopSecundair} ${styles.rood}`}
                      onClick={handleVerwijderOverride}>
                      ✕ Override verwijderen
                    </button>
                  )}
                </div>
              </div>

              {/* Versiegeschiedenis */}
              {toonVersies && (
                <div className={styles.versiesWrap}>
                  <div className={styles.versiesTitel}>
                    Versiegeschiedenis
                    <button className={styles.versiesSluit} onClick={() => setToonVersies(false)}>✕</button>
                  </div>
                  {versiesLaden ? (
                    <div className={styles.laadTekst}>Laden...</div>
                  ) : versies.length === 0 ? (
                    <div className={styles.laadTekst}>Nog geen eerdere versies opgeslagen.</div>
                  ) : (
                    versies.map(v => (
                      <div key={v.id} className={styles.versieRij}>
                        <div className={styles.versieInfo2}>
                          <span className={styles.versieNr}>v{v.versie}</span>
                          <span className={styles.versieDatum}>
                            {new Date(v.opgeslagen_op).toLocaleString('nl-NL')}
                          </span>
                          {v.opgeslagen_door_naam && (
                            <span className={styles.versieDoor}>door {v.opgeslagen_door_naam}</span>
                          )}
                          {v.notitie && <span className={styles.versieNotitie}>"{v.notitie}"</span>}
                        </div>
                        <button className={styles.knopKlein}
                          onClick={() => handleTerugzetten(v.id, v.versie)}>
                          ↩ Terugzetten
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Systeem prompt */}
              <div className={styles.veld}>
                <label className={styles.veldLabel}>
                  Systeem prompt
                  <span className={styles.veldUitleg}>Gedragsregels en persoonlijkheid van de AI</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={systeemPrompt}
                  onChange={e => { setSysteemPrompt(e.target.value); setGewijzigd(true); }}
                  rows={8}
                />
              </div>

              {/* Gebruiker prompt */}
              <div className={styles.veld}>
                <label className={styles.veldLabel}>
                  Gebruiker prompt
                  <span className={styles.veldUitleg}>Taakinstructie met structuur en gewenste output</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={gebruikerPrompt}
                  onChange={e => { setGebruikerPrompt(e.target.value); setGewijzigd(true); }}
                  rows={6}
                />
              </div>

              {/* Notitie */}
              <div className={styles.veld}>
                <label className={styles.veldLabel}>
                  Notitie bij opslaan <span className={styles.optioneel}>(optioneel)</span>
                </label>
                <input type="text" className={styles.input}
                  value={notitie}
                  onChange={e => setNotitie(e.target.value)}
                  placeholder="bijv. Aanbeveling sectie uitgebreid" />
              </div>

              <div className={styles.opslaanRij}>
                {gewijzigd && <span className={styles.gewijzigdIndicator}>● Niet opgeslagen</span>}
                <button className={styles.knopPrimair}
                  onClick={handleOpslaan}
                  disabled={opslaan || !gewijzigd}>
                  {opslaan ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
